import { createPermissions, hasRole } from 'cesg-shared';
import { type SessionUser, UserRole } from 'cesg-shared/dist/models';
import type { Ref } from 'vue';
import { computed } from 'vue';

type ButtonState = 'visible' | 'hidden';
type InputState = 'edit' | 'read-only' | 'hidden';

export interface LiftPlanPermissions {
  components: {
    deleteEntry: ButtonState;
    deleteSubtotal: ButtonState;
    deleteAta: ButtonState;
    addLift: ButtonState;
    addRow: ButtonState;
    addAta: ButtonState;
    contractSum: InputState;
    retentionPercentage: InputState;
    sectionCard: {
      edit: ButtonState;
      title: InputState;
      delete: ButtonState;
    };
    retentionAmount: InputState;
    entreprenadsumma: InputState;
  };
  columns: Record<string, InputState>;
  subtotalColumns: Record<string, InputState>;
  ataColumns: Record<string, InputState>;
}

interface UseLiftPlanPermissionsProps {
  isEditing: Ref<boolean>;
  projectId: string;
  user: SessionUser;
}

export const useLiftPlanUEPermissions = ({
  isEditing,
  user,
  projectId,
}: UseLiftPlanPermissionsProps) => {
  return computed<LiftPlanPermissions>(() => {
    const buttonState: ButtonState = isEditing.value ? 'visible' : 'hidden';
    const inputState: InputState = isEditing.value ? 'edit' : 'read-only';
    const permissions = createPermissions(projectId, user);

    return {
      components: {
        deleteEntry: buttonState,
        deleteSubtotal: buttonState,
        deleteAta: buttonState,
        addLift: buttonState,
        addRow: buttonState,
        addAta: buttonState,
        contractSum: inputState,
        retentionPercentage: inputState,
        retentionAmount: 'read-only',
        entreprenadsumma: 'read-only',
        sectionCard: {
          edit:
            permissions.edit.overview && hasRole(UserRole.CESG, user)
              ? 'visible'
              : 'hidden',
          title: inputState,
          delete:
            permissions.edit.overview && hasRole(UserRole.CESG, user)
              ? 'visible'
              : 'hidden',
        },
      },
      columns: {
        number: 'read-only',
        description: inputState,
        dueDate: inputState,
        amount: inputState,
        retention: inputState,
        amountDue: inputState,
        invoicedDate: inputState,
        paymentDate: inputState,
        comment: inputState,
      },
      subtotalColumns: {
        label: inputState,
        amount: inputState,
        invoicedDate: inputState,
        paymentDate: inputState,
        comment: inputState,
      },
      ataColumns: {
        label: inputState,
        amount: inputState,
        invoicedDate: inputState,
        paymentDate: inputState,
        comment: inputState,
      },
    };
  });
};
